import React from 'react'
import { Show, TextField, DateField } from '@refinedev/antd'
import { useShow } from '@refinedev/core'
import { Typography, List as AntList, Divider, Tag } from 'antd'
import type { Tables } from '@muaa/shared'
import {
  RELEASE_ITEM_KIND_ORDER,
  RELEASE_ITEM_KIND_GROUP_LABEL,
  RELEASE_ITEM_KIND_TAG_COLOR,
  parseReleaseItems,
} from './types'

type Release = Tables<'releases'>

const { Title, Text } = Typography

/**
 * Read-only preview of a release exactly as a member would see it in the
 * mobile "What's New" screen: grouped New / Improved / Fixed, in that fixed
 * order, so an admin can sanity-check the wording before publishing.
 */
export function ReleaseShow() {
  const { query } = useShow<Release>()
  const isLoading = query.isLoading
  const record = query.data?.data
  const items = parseReleaseItems(record?.items)
  const isPublished = Boolean(record?.published_at)

  return (
    <Show isLoading={isLoading}>
      {/* ── Header ─────────────────────────────────────────── */}
      <Divider orientation="left">Header</Divider>
      <Title level={5}>Title</Title>
      <TextField value={record?.title ?? '—'} />
      <Title level={5} style={{ marginTop: 12 }}>
        Summary
      </Title>
      <TextField value={record?.summary ?? '—'} />

      <Title level={5} style={{ marginTop: 12 }}>
        Status
      </Title>
      {isPublished ? <Tag color="green">Published</Tag> : <Tag color="gold">Draft</Tag>}
      {record?.show_in_feed ? <Tag color="blue">Shows in feed</Tag> : null}

      {record?.published_at ? (
        <>
          <Title level={5} style={{ marginTop: 12 }}>
            Published
          </Title>
          <DateField value={record.published_at} format="MMM D, YYYY h:mm A" />
        </>
      ) : (
        <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
          This is a draft — not visible to members.
        </Text>
      )}

      {/* ── What changed — grouped exactly as members see it ── */}
      <Divider orientation="left">What changed</Divider>
      {items.length > 0 ? (
        RELEASE_ITEM_KIND_ORDER.map((kind) => {
          const kindItems = items.filter((item) => item.kind === kind)
          if (kindItems.length === 0) return null
          return (
            <div key={kind} style={{ marginBottom: 16 }}>
              <Tag color={RELEASE_ITEM_KIND_TAG_COLOR[kind]}>{RELEASE_ITEM_KIND_GROUP_LABEL[kind]}</Tag>
              <AntList
                dataSource={kindItems}
                renderItem={(item, index) => (
                  <AntList.Item key={index} style={{ display: 'block', paddingBottom: 6, paddingLeft: 4 }}>
                    <Typography.Paragraph style={{ marginBottom: 0 }}>{item.text}</Typography.Paragraph>
                  </AntList.Item>
                )}
                size="small"
                split={false}
              />
            </div>
          )
        })
      ) : (
        <TextField value="No items yet." />
      )}

      <Divider />
      <Title level={5}>Created</Title>
      {record?.created_at ? (
        <DateField value={record.created_at} format="MMM D, YYYY h:mm A" />
      ) : (
        <TextField value="—" />
      )}
    </Show>
  )
}
